import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export function ContactForm() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", { name: name.trim(), email: email.trim(), message: message.trim() });
      setDone(true);
      toast.success(t("contact.success"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
      className="glass frame-pulse rounded-3xl p-8 max-w-xl mx-auto" data-testid="contact-form"
    >
      {done ? (
        <div className="text-center py-6" data-testid="contact-success">
          <CheckCircle2 className="text-primary mx-auto mb-4" size={34} />
          <p className="font-display text-xl text-foreground/85">{t("contact.success")}</p>
        </div>
      ) : (
        <>
          <h3 className="font-display text-2xl text-gradient-orange mb-1 text-center">{t("contact.formTitle")}</h3>
          <p className="text-foreground/55 text-sm mb-6 text-center">{t("contact.formSubtitle")}</p>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("contact.namePh")} maxLength={80}
                className="bg-black/30 border border-white/15 rounded-full px-5 py-3 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors"
              />
              <input
                data-testid="contact-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("contact.emailPh")}
                className="bg-black/30 border border-white/15 rounded-full px-5 py-3 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors"
              />
            </div>
            <textarea
              data-testid="contact-message" required value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={t("contact.messagePh")} rows={4} maxLength={4000}
              className="w-full bg-black/30 border border-white/15 rounded-2xl px-5 py-3 outline-none text-foreground placeholder:text-foreground/30 resize-none focus:border-primary/50 transition-colors"
            />
            <div className="flex justify-center pt-1">
              <button
                type="submit" disabled={sending} data-testid="contact-submit"
                className="btn-frost-orange inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm disabled:opacity-50"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                {sending ? t("contact.sending") : t("contact.send")}
              </button>
            </div>
          </form>
        </>
      )}
    </motion.div>
  );
}
